import React from 'react';
import OptimizedImage from './OptimizedImage';
import DefaultAvatar from './DefaultAvatar';
import { hasValidProfileImage } from '../utils/avatarUtils';
import '../styles/HighlightedAvatar.css';

// Use WebP version if available, fallback to PNG
const SELECTED_USER_RING = '/selected_user_ring.webp';
const SELECTED_USER_RING_FALLBACK = '/selected_user_ring.png';

/**
 * HighlightedAvatar - A component that displays a profile image with an optional
 * golden ring with crown decoration for highlighted/selected users
 * 
 * @param {string} src - Image source URL
 * @param {string} alt - Alt text for the image
 * @param {string} className - Additional CSS classes
 * @param {boolean} isHighlighted - Whether to show the golden ring with crown
 * @param {boolean} isPrivate - Whether the user is private (uses default avatar)
 * @param {string} username - Username for generating default avatar initials
 * @param {string|number} userId - User ID for consistent avatar color
 * @param {object} imageProps - Additional props to pass to OptimizedImage
 */
const HighlightedAvatar = ({
    src,
    alt = 'Avatar',
    className = '',
    isHighlighted = false,
    isPrivate = false,
    username = null,
    userId = null,
    imageProps = {},
    ...props
}) => {
    // Determine if we should show the default avatar or the actual image
    const showDefaultAvatar = isPrivate || !hasValidProfileImage(src);
    
    // Extract username from alt if not provided
    const displayName = username || alt || 'User';
    // Use userId or username for consistent coloring
    const identifier = userId || username || alt;

    return (
        <div 
            className={`highlighted-avatar ${isHighlighted ? 'highlighted-avatar--highlighted' : ''} ${className}`}
            {...props}
        >
            {isHighlighted && (
                <picture className="highlighted-avatar__ring">
                    <source srcSet={SELECTED_USER_RING} type="image/webp" />
                    <img 
                        src={SELECTED_USER_RING_FALLBACK}
                        alt=""
                        className="highlighted-avatar__ring-image"
                        aria-hidden="true"
                    />
                </picture>
            )}
            <div className="highlighted-avatar__image-wrapper">
                {showDefaultAvatar ? (
                    <DefaultAvatar
                        name={displayName}
                        identifier={identifier}
                        className="highlighted-avatar__image highlighted-avatar__default"
                    />
                ) : (
                    <OptimizedImage
                        src={src}
                        alt={alt}
                        className="highlighted-avatar__image"
                        placeholder={imageProps.placeholder || "blur"}
                        {...imageProps}
                    />
                )}
            </div>
        </div>
    );
};

export default HighlightedAvatar;
